import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { userServices } from "../../services/UserServices";
import { toast } from "react-toastify";
import { MEDIA_URL } from "../../global_constants";

const UserForm = () => {
  const [fullName, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState("active");

  const [kycStatus, setKycStatus] = useState("pending");
  const [identificationDocument, setIdentificationDocument] = useState("");
  const [identificationVerified, setIdentificationVerified] = useState(false);
  const [proofOfAddressDocument, setProofOfAddressDocument] = useState("");
  const [proofOfAddressVerified, setProofOfAddressVerified] = useState(false);
  const [selfieUpload, setSelfieUpload] = useState("");
  const [selfieVerified, setSelfieVerified] = useState(false);


  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) fetchUser();
  }, [id]);

  const fetchUser = async () => {
    try {
      const response = await userServices.getUserDetails(id);
      if (response.status === 200 && response.data) {
        const user = response.data;
        setFullName(user.fullName || "");
        setEmail(user.email || "");
        setPhoneNumber(user.phoneNumber || "");
        setStatus(user.status || "active");
        setKycStatus(user.kyc_status || "pending");
        setIdentificationDocument(user.identificationDocument || "");
        setIdentificationVerified(user.identificationDocument_verified || false);
        setProofOfAddressDocument(user.proofOfAddressDocument || "");
        setProofOfAddressVerified(user.proofOfAddressDocument_verified || false);
        setSelfieUpload(user.selfieUpload || "");
        setSelfieVerified(user.selfieUpload_verified || false);
      } else {
        toast.error("Failed to load user");
      }
    } catch (error) {
      toast.error("Error fetching user details");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        fullName,
        phoneNumber,
        email,
        status,
      };
      data.kyc_status = kycStatus;
      data.identificationDocument_verified = identificationVerified;
      data.proofOfAddressDocument_verified = proofOfAddressVerified;
      data.selfieUpload_verified = selfieVerified;


      if (!id) {
        data.password = password;
        const response = await userServices.addUser(data);
        if (response.status === 201) {
          toast.success("User created successfully");
          navigate("/users");
        } else {
          toast.error(response.data.message);
        }
      } else {
        const response = await userServices.updateUser(id, data);
        if (response.status === 200) {
          toast.success("User updated successfully");
          navigate("/users");
        } else {
          toast.error(response.data.message);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong");
    }
  };

  return (
    <div className="container-xxl py-3">
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5>{id ? "Edit User" : "Add New User"}</h5>
          <Link to="/users" className="btn btn-sm btn-secondary">Back</Link>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Full Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Phone Number</label>
                <input
                  type="tel"
                  className="form-control"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              </div>
              {!id && (
                <div className="col-md-6">
                  <label className="form-label">Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              )}
              <div className="col-md-6">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <hr />
              <h6 className="fw-bold">KYC Verification</h6>

              <div className="col-md-6">
                <label className="form-label">KYC Status</label>
                <select
                  className="form-select"
                  value={kycStatus}
                  onChange={(e) => setKycStatus(e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              {/* Identification Document */}
              {identificationDocument && (
                <div className="col-md-6">
                  <label className="form-label">Identification Document</label>
                  <div>
                    <img src={`${MEDIA_URL}/${identificationDocument}`} alt="ID Document" className="img-fluid rounded mb-2" style={{ maxHeight: "200px" }} />
                  </div>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="idVerified"
                      checked={identificationVerified}
                      onChange={() => setIdentificationVerified(!identificationVerified)}
                    />
                    <label className="form-check-label" htmlFor="idVerified">Verified</label>
                  </div>
                </div>
              )}

              {/* Proof of Address Document */}
              {proofOfAddressDocument && (
                <div className="col-md-6">
                  <label className="form-label">Proof of Address</label>
                  <div>
                    <img src={`${MEDIA_URL}/${proofOfAddressDocument}`} alt="Proof of Address" className="img-fluid rounded mb-2" style={{ maxHeight: "200px" }} />
                  </div>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="addressVerified"
                      checked={proofOfAddressVerified}
                      onChange={() => setProofOfAddressVerified(!proofOfAddressVerified)}
                    />
                    <label className="form-check-label" htmlFor="addressVerified">Verified</label>
                  </div>
                </div>
              )}

              {/* Selfie Upload */}
              {selfieUpload && (
                <div className="col-md-6">
                  <label className="form-label">Selfie</label>
                  <div>
                    <img src={`${MEDIA_URL}/${selfieUpload}`} alt="Selfie" className="img-fluid rounded mb-2" style={{ maxHeight: "200px" }} />
                  </div>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="selfieVerified"
                      checked={selfieVerified}
                      onChange={() => setSelfieVerified(!selfieVerified)}
                    />
                    <label className="form-check-label" htmlFor="selfieVerified">Verified</label>
                  </div>
                </div>
              )}
            </div>

            <button type="submit" className="btn btn-primary mt-4">
              {id ? "Update User" : "Create User"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserForm;
